import { ToggleButton } from "@src/appV2/redesign/system/ui-components";

import { FilterSectionWrapper } from "../../../ShiftDiscovery/Filters/BottomSheet/FilterSectionWrapper";
import { FiltersToggleButtonGroup } from "../../../ShiftDiscovery/Filters/BottomSheet/ToggleButtonGroup";
import {
  FILTER_ALL_VALUE,
  useFilterSelectedOptions,
} from "../../../ShiftDiscovery/Filters/BottomSheet/useFilterSelectedOptions";
import { CARE_TYPES, SHIFT_ROLES, VISIT_TYPE_ENTRIES } from "../constants";
import { type VisitFilters } from "../types";

interface ShiftRolesFilterProps {
  value: VisitFilters["shiftRoles"];
  onChange: (value: VisitFilters["shiftRoles"]) => void;
}

export function ShiftRolesFilter(props: ShiftRolesFilterProps) {
  const { value, onChange } = props;

  const { selectedOptions, onChangeSelection } = useFilterSelectedOptions(value, onChange, [
    ...SHIFT_ROLES,
  ]);

  return (
    <FilterSectionWrapper sectionTitle="Visit Roles">
      <FiltersToggleButtonGroup
        value={selectedOptions}
        aria-label="Visit Roles Filter Toggle Buttons"
        onChange={(newValue) => {
          onChangeSelection(newValue ?? []);
        }}
      >
        <ToggleButton value={FILTER_ALL_VALUE}>All</ToggleButton>
        {SHIFT_ROLES.map((role) => {
          const isSelected =
            selectedOptions.includes(role) || selectedOptions.includes(FILTER_ALL_VALUE);
          return (
            <ToggleButton
              key={role}
              value={role}
              selected={isSelected}
              iconType={isSelected ? "checkmark" : undefined}
            >
              {role}
            </ToggleButton>
          );
        })}
      </FiltersToggleButtonGroup>
    </FilterSectionWrapper>
  );
}

interface CareTypeFilterProps {
  value: VisitFilters["excludedCareTypes"];
  onChange: (value: VisitFilters["excludedCareTypes"]) => void;
}

export function CareTypeFilter(props: CareTypeFilterProps) {
  const { value: excludedCareTypes, onChange } = props;

  return (
    <FilterSectionWrapper sectionTitle="Care Type" sectionSubtitle="Tap to exclude care types">
      <FiltersToggleButtonGroup
        value={excludedCareTypes}
        aria-label="Care Type Filter Toggle Buttons"
        onChange={(newValue) => {
          onChange(newValue ?? []);
        }}
      >
        {CARE_TYPES.map((careType) => {
          const isExcluded = excludedCareTypes.includes(careType);
          return (
            <ToggleButton
              key={careType}
              value={careType}
              selected={isExcluded}
              iconType={isExcluded ? "close" : undefined}
              sx={isExcluded ? { "& > span": { textDecoration: "line-through" } } : undefined}
            >
              {careType}
            </ToggleButton>
          );
        })}
      </FiltersToggleButtonGroup>
    </FilterSectionWrapper>
  );
}

interface VisitTypeFilterProps {
  value: VisitFilters["visitTypes"];
  onChange: (value: VisitFilters["visitTypes"]) => void;
}

export function VisitTypeFilter(props: VisitTypeFilterProps) {
  const { value, onChange } = props;

  const allVisitTypeKeys = VISIT_TYPE_ENTRIES.map(([key]) => key);
  const { selectedOptions, onChangeSelection } = useFilterSelectedOptions(
    value,
    onChange,
    allVisitTypeKeys
  );

  return (
    <FilterSectionWrapper sectionTitle="Visit Type">
      <FiltersToggleButtonGroup
        value={selectedOptions}
        aria-label="Visit Type Filter Toggle Buttons"
        onChange={(newValue) => {
          onChangeSelection(newValue ?? []);
        }}
      >
        <ToggleButton value={FILTER_ALL_VALUE}>All</ToggleButton>
        {VISIT_TYPE_ENTRIES.map(([key, label]) => {
          const isSelected =
            selectedOptions.includes(key) || selectedOptions.includes(FILTER_ALL_VALUE);
          return (
            <ToggleButton
              key={key}
              value={key}
              selected={isSelected}
              iconType={isSelected ? "checkmark" : undefined}
            >
              {label}
            </ToggleButton>
          );
        })}
      </FiltersToggleButtonGroup>
    </FilterSectionWrapper>
  );
}
